export const CHECKOUT_URL = "https://postendo-en.impultienda.ar/checkout/";

export const images = {
  mockup: "https://cdn.magicpatterns.com/uploads/uFKWQqTQj2Z9AGVdoaynx4/MOCKUP_POSTENDO.png",
  author: "https://cdn.magicpatterns.com/uploads/59jDbk9PQrWn1ccAViNq6V/Dr._Alexander_Hayes_-_ENDODONCIA.png",
};

export const planningSteps = [
  { label: "Assess", text: "Review the remaining tooth structure and relevant clinical findings." },
  { label: "Classify", text: "Organize the restorative challenges and limitations." },
  { label: "Evaluate", text: "Consider ferrule, structural integrity, functional demands, and other relevant factors." },
  { label: "Compare", text: "Review potential restorative approaches and their considerations." },
  { label: "Plan", text: "Organize the proposed restorative sequence." },
  { label: "Review", text: "Check for overlooked factors before finalizing your plan." },
];

export const considerQuestions = [
  "How much sound tooth structure remains?",
  "Is there sufficient coronal structure for a predictable restoration?",
  "When might a post be indicated — and when might it be unnecessary?",
  "How should the ferrule effect influence restorative planning?",
  "Which restorative options deserve consideration?",
  "What role do occlusal forces and functional demands play?",
  "Which material and adhesive considerations matter?",
  "What potential complications should be evaluated before proceeding?",
  "How will you organize the restorative sequence?",
];

export const beforeItems = [
  "You review multiple sources before organizing your restorative approach.",
  "You struggle to determine which clinical factors deserve priority.",
  "You aren’t always sure how the remaining tooth structure should influence your options.",
  "You may focus on selecting a post before evaluating whether one is necessary.",
  "You find it difficult to compare restorative alternatives systematically.",
  "You sometimes overlook important considerations until later in the process.",
  "Every unfamiliar case feels like starting from scratch.",
];

export const afterItems = [
  "You begin with a structured assessment of the tooth.",
  "You organize the remaining tooth structure and restorative limitations.",
  "You evaluate ferrule considerations before selecting a restorative approach.",
  "You distinguish situations that may require a post from those where another approach may be appropriate.",
  "You compare restorative options using organized clinical considerations.",
  "You follow a structured planning sequence.",
  "You have reusable worksheets and checklists to support your reasoning.",
];

export type PainIcon = "compass" | "clock" | "tooth" | "ruler" | "scale" | "alert";

export const pains: { icon: PainIcon; title: string; text: string }[] = [
  { icon: "compass", title: "You’re unsure where to start.", text: "You have a tooth that has undergone root canal treatment, but you’re uncertain which restorative factors should be evaluated first." },
  { icon: "clock", title: "You spend too much time reviewing scattered information.", text: "You consult textbooks, notes, videos, and previous cases before you can organize your restorative plan." },
  { icon: "tooth", title: "You struggle with the post-or-no-post decision.", text: "You understand what an intraradicular post does, but you’re still working on identifying when it may be indicated." },
  { icon: "ruler", title: "You want to understand the ferrule effect better.", text: "You know the remaining tooth structure matters, but you want a clearer framework for incorporating it into restorative planning." },
  { icon: "scale", title: "You want to make better-informed restorative decisions.", text: "You want to compare available approaches before committing to a treatment plan." },
  { icon: "alert", title: "You worry about overlooking important factors.", text: "You want a repeatable process for reviewing structural, functional, material-related, and procedural considerations." },
];

export const volumes = [
  {
    numeral: "I",
    title: "The Post-Endodontic Assessment Blueprint",
    tagline: "Start With the Tooth, Not the Restoration.",
    intro: "Before considering a post, core, or crown, you need to understand the condition of the tooth. I’ll guide you through the factors to consider when evaluating an endodontically treated tooth and organizing its restorative needs.",
    bullets: ["Remaining coronal tooth structure.", "Existing restorations and structural defects.", "Relevant clinical and radiographic findings.", "Functional and occlusal considerations.", "Factors that may influence restorability.", "Questions to resolve before proceeding with restorative planning."],
    outro: "The objective is to help you organize your findings before choosing a restorative approach.",
  },
  {
    numeral: "II",
    title: "The Ferrule Effect & Remaining Tooth Structure Guide",
    tagline: "Understand the Structure That Can Influence Your Restorative Strategy.",
    intro: "One of the most important considerations in restoring an endodontically treated tooth is the amount and configuration of remaining sound tooth structure. I’ll explain the ferrule effect and how it fits into restorative planning.",
    bullets: ["What the ferrule effect means.", "Why remaining axial tooth structure matters.", "How ferrule considerations influence restorative options.", "The relationship between structural limitations and restoration design.", "Factors to assess when the ideal ferrule cannot be achieved.", "Questions to consider before finalizing your restorative plan."],
    outro: "My goal is to help you understand the structural factors that deserve attention before you select a restoration.",
  },
  {
    numeral: "III",
    title: "Post or No Post? The Clinical Decision Guide",
    tagline: "Understand When a Post May Be Necessary — and When It May Not Be.",
    intro: "A common misconception is that every endodontically treated tooth requires an intraradicular post. That’s not the case. A post primarily helps retain a core when sufficient coronal tooth structure is unavailable. It does not automatically strengthen the root or eliminate the risk of fracture.",
    bullets: ["The purpose and limitations of intraradicular posts.", "The relationship between remaining tooth structure and core retention.", "Factors that may influence post indication.", "Differences between post retention and tooth reinforcement.", "Fiber posts and other post-system considerations.", "Important root anatomy and procedural risks.", "Questions to review before selecting a post system."],
    outro: "You’ll learn to approach the post-or-no-post decision as a clinical assessment, not an automatic step.",
  },
  {
    numeral: "IV",
    title: "Core Build-Up & Definitive Restoration Selection",
    tagline: "Connect the Reconstructive Phase With the Final Restoration.",
    intro: "Once you’ve assessed the tooth and considered whether a post is needed, the next challenge is organizing the reconstructive strategy.",
    bullets: ["Core build-up objectives and limitations.", "General considerations for selecting restorative materials.", "Direct and indirect restorative approaches.", "Factors influencing the selection of full-coverage restorations and other alternatives.", "Adhesive and cementation considerations.", "Functional demands and occlusal factors.", "How the restorative plan should account for the remaining tooth structure."],
    outro: "The goal is to help you compare options systematically rather than selecting materials based on habit alone.",
  },
  {
    numeral: "V",
    title: "Preventable Restorative Complications & Clinical Review",
    tagline: "Identify Important Risks Before They Become Procedural Problems.",
    intro: "Some restorative complications are influenced by decisions made before the procedure begins. I’ll help you organize a pre-restorative review of factors that deserve attention.",
    bullets: ["Insufficient remaining tooth structure.", "Inadequate ferrule conditions.", "Inappropriate post selection.", "Potential root damage during post-space preparation.", "Adhesive and cementation challenges.", "Fracture-related risk factors.", "Occlusal and functional considerations.", "Potential problems with the restorative sequence."],
    outro: "You’ll also use a structured review checklist to identify questions that may require further assessment, plan modification, or specialist consultation. The objective is better preparation, not a promise that every complication can be prevented.",
  },
  {
    numeral: "VI",
    title: "The Complete Post-Endodontic Restorative Roadmap",
    tagline: "Turn Your Findings Into an Organized Restorative Plan.",
    intro: "In this final volume, I’ll show you how to organize the information from your assessment into a single restorative planning document. Your roadmap will help you record:",
    bullets: ["Tooth assessment and relevant findings.", "Remaining tooth structure.", "Restorative challenges and limitations.", "Ferrule considerations.", "Post indication and rationale, where applicable.", "Core and definitive restoration options.", "Material-related considerations.", "Potential risks and unresolved questions.", "Proposed restorative sequence.", "Follow-up and reassessment considerations."],
    outro: "This is where the individual concepts become one organized planning framework.",
  },
];

export type ResourceIcon = "clipboard" | "tooth" | "table" | "file" | "ruler";

export const resources: { icon: ResourceIcon; title: string; text: string }[] = [
  { icon: "clipboard", title: "Professional Assessment Checklists", text: "Reusable checklists to help you review remaining tooth structure, restorative limitations, ferrule considerations, and other relevant factors." },
  { icon: "tooth", title: "Clinical Decision Frameworks", text: "Visual decision-support diagrams that help you organize the questions to consider before selecting a restorative approach." },
  { icon: "table", title: "Restorative Comparison Matrices", text: "Structured worksheets for comparing potential restorative options, material considerations, advantages, limitations, and case-specific factors." },
  { icon: "file", title: "Treatment Planning Worksheets", text: "Practical templates to document findings, identify unresolved questions, and organize the proposed restorative sequence." },
  { icon: "ruler", title: "Visual Restorative Guides", text: "Educational diagrams explaining key concepts related to post retention, core reconstruction, ferrule, and definitive restoration planning." },
];

export const bonuses = [
  {
    number: 1,
    title: "The 30 Post-Endodontic Case Decision Cards™",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/uRdQHtuazG7RZJKBzwUkDP/BONUS_1_POSTENDO-EN.png",
    text: "Thirty educational case prompts designed to help you practice restorative reasoning. Each card presents a clinical scenario, the main considerations to evaluate, and questions to guide your analysis — remaining tooth structure, ferrule, post indication, core reconstruction, and definitive restoration selection.",
    why: "I want you to practice applying concepts rather than simply reading about them.",
  },
  {
    number: 2,
    title: "The 10-Minute Restorative Assessment Worksheet™",
    value: 27,
    image: "https://cdn.magicpatterns.com/uploads/nV3GJKbWhsTex1VvU8T7FH/BONUS_2_POSTENDO-EN.png",
    text: "A structured worksheet that helps you organize the initial assessment of an endodontically treated tooth. Work through the relevant findings, remaining tooth structure, restorative challenges, and questions requiring further evaluation.",
    why: "The suggested time refers to completing the initial worksheet when sufficient information is available, not performing a comprehensive clinical examination.",
  },
  {
    number: 3,
    title: "The Ferrule & Post Indication Audit™",
    value: 29,
    image: "https://cdn.magicpatterns.com/uploads/5rW9REFjfX6DGxzU4Kj26o/BONUS_3_POSTENDO-EN.png",
    text: "A dedicated review checklist covering two important aspects of post-endodontic restoration planning: remaining tooth structure, ferrule considerations, potential post-retention needs, and relevant anatomical and procedural limitations.",
    why: "These decisions deserve deliberate evaluation rather than automatic assumptions.",
  },
  {
    number: 4,
    title: "The Post-Endodontic Restoration Mistake Library™",
    value: 27,
    image: "https://cdn.magicpatterns.com/uploads/wb6BgPzCsbZ4hyBL2cL4qa/BONUS_4_POSTENDO-EN.png",
    text: "An educational collection of common restorative planning pitfalls involving structural assessment, post selection, core reconstruction, material considerations, and definitive restoration planning. Each topic includes review questions to help you recognize potential problems before proceeding.",
    why: "It’s not to suggest every complication is avoidable — it’s to help you identify considerations that may otherwise be overlooked.",
  },
  {
    number: 5,
    title: "The One-Page Restorative Case Planner™",
    value: 27,
    image: "https://cdn.magicpatterns.com/uploads/vPpx5aqG1RS7Z5PGixJwR6/BONUS_5_POSTENDO-EN.png",
    text: "A professional planning template that brings the key elements of your assessment together on one page: clinical findings, remaining tooth structure, ferrule, post indication and rationale, core reconstruction, definitive restoration options, potential risks, and proposed sequence and follow-up.",
    why: "Use it as a reusable educational planning aid when reviewing suitable cases.",
  },
];

export const offerIncludes = [
  "Immediate digital access",
  "All 6 premium volumes",
  "All 5 exclusive bonuses",
  "Reusable digital planning resources",
  "Lifetime access to the purchased materials",
  "7-Day Money-Back Guarantee",
];

export const guaranteeItems = [
  "Explore all six volumes.",
  "Review the post-endodontic assessment framework.",
  "Study the ferrule and remaining-tooth-structure material.",
  "Examine the post indication decision guide.",
  "Use the assessment worksheets and checklists.",
  "Review the restorative comparison matrices.",
  "Explore the one-page case planner.",
];

export const testimonials = [
  {
    name: "Dr. Camila Restrepo",
    role: "General Dentist",
    location: "Medellín, Colombia",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/bdcc696f-87c9-4f7e-b0fb-246019b5d866.jpg",
    quote: "The assessment worksheet finally gave my findings an order. I write down the remaining structure, the ferrule situation and my open questions before I even think about materials.",
  },
  {
    name: "Dr. Martín Aguirre",
    role: "Restorative Dentist",
    location: "Córdoba, Argentina",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/48a73bc2-b705-48fa-bf8d-dcb2e00aac81.jpg",
    quote: "The ferrule guide clarified a concept I thought I already understood. Seeing how it connects to restoration design changed the way I review each case.",
  },
  {
    name: "Lucía Fernández",
    role: "Dental Student, 5th year",
    location: "Madrid, Spain",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/afe138c1-bd89-4806-8c89-c2e3f5b4a16f.jpg",
    quote: "The post-or-no-post framework was exactly what I needed for my clinical rotations. I stopped assuming every treated tooth needs a post.",
  },
  {
    name: "Dr. Ricardo Salinas",
    role: "General Dentist",
    location: "Monterrey, Mexico",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/3454fc9c-e917-477c-bf0f-18a38c436628.jpg",
    quote: "The one-page case planner and the comparison matrices make it much easier to document my reasoning and compare alternatives side by side.",
  },
];

export const aboutSteps = [
  { title: "Understand the tooth.", text: "Start by organizing the clinical findings and evaluating the remaining structure." },
  { title: "Identify the limitations.", text: "Consider structural defects, ferrule conditions, functional demands, and other relevant factors." },
  { title: "Evaluate the restorative options.", text: "Understand the role and limitations of posts, cores, and definitive restorations." },
  { title: "Compare the alternatives.", text: "Organize the advantages, limitations, and considerations associated with different approaches." },
  { title: "Review the potential risks.", text: "Identify questions that need further investigation before proceeding." },
  { title: "Build the restorative roadmap.", text: "Bring the relevant findings and decisions together in a structured plan." },
];

export const faqs = [
  { q: "How will I receive the materials?", a: "Immediately after your payment, you’ll receive instructions to access the digital product. The six volumes and five bonuses will be provided in the formats specified at checkout." },
  { q: "Is Post-Endodontic Restoration Blueprint™ a course?", a: "No. It’s a digital educational resource consisting of six volumes, five bonuses, and practical planning materials. It is designed for self-paced reading and reference." },
  { q: "Is this product intended for general dentists or specialists?", a: "The material is designed primarily for general dentists, restorative dentistry professionals, and dental students seeking to deepen their understanding of post-endodontic restorative principles. The complexity of individual cases may require specialist assessment or consultation." },
  { q: "Does every endodontically treated tooth need a post?", a: "No. A post is generally considered when additional retention for a core is needed because insufficient coronal tooth structure remains. The decision depends on the individual tooth, its structural condition, and the proposed restoration. The product explains the relevant concepts but does not provide a universal rule for every case." },
  { q: "Will this product tell me which restoration to use for every patient?", a: "It provides educational frameworks and tools to help you organize the relevant considerations. The final decision depends on the patient’s clinical findings, the tooth’s condition, the available restorative options, and professional judgment." },
  { q: "Can I use the worksheets more than once?", a: "Yes. The planning worksheets and checklists are designed to be reusable for personal educational and clinical-planning reference, subject to the product’s usage terms." },
  { q: "Is this a one-time payment?", a: "Yes. The launch offer is a one-time payment of $27 USD, with no recurring subscription. Currency conversion, if applicable, is calculated automatically according to your country at checkout." },
  { q: "Do I get lifetime access?", a: "Yes. Your purchase includes ongoing access to the purchased digital materials, subject to the platform’s availability and the applicable terms." },
  { q: "What if I don’t like the product?", a: "Your purchase includes a seven-day money-back guarantee. Review the refund instructions provided at checkout and contact us within the applicable period if you decide the product isn’t right for you." },
];

export const buyers = [
  { name: "Valentina", country: "Colombia", flag: "🇨🇴" },
  { name: "Santiago", country: "Argentina", flag: "🇦🇷" },
  { name: "Daniela", country: "Mexico", flag: "🇲🇽" },
  { name: "Mateo", country: "Chile", flag: "🇨🇱" },
  { name: "Camila", country: "Peru", flag: "🇵🇪" },
  { name: "Javier", country: "Spain", flag: "🇪🇸" },
  { name: "Gabriela", country: "Ecuador", flag: "🇪🇨" },
  { name: "Andrés", country: "Uruguay", flag: "🇺🇾" },
  { name: "Mariana", country: "Venezuela", flag: "🇻🇪" },
  { name: "Diego", country: "Guatemala", flag: "🇬🇹" },
  { name: "Sofía", country: "Costa Rica", flag: "🇨🇷" },
  { name: "Luis", country: "Dominican Republic", flag: "🇩🇴" },
  { name: "Paula", country: "Bolivia", flag: "🇧🇴" },
  { name: "Fernando", country: "Paraguay", flag: "🇵🇾" },
  { name: "Isabella", country: "Panama", flag: "🇵🇦" },
];
